import { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chart, registerables } from 'chart.js';
import { dashboardService, DashboardStats, TimeSeriesPoint, TopProduct } from '@/services/dashboard.service';
import { Alert } from '@/components/common/Alert';
import { FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import '@/styles/dashboard.css';
import '@/styles/variables.css';

Chart.register(...registerables);

// Fonction pour formater les montants
const formatCurrency = (amount: number, locale: string): string => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Fonction pour formater les nombres
const formatNumber = (num: number): string => {
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

// Fonction pour obtenir le nom du jour
const getDayName = (date: Date, locale: string): string => {
  return new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(date);
};

export const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith('fr') ? 'fr-FR' : 'en-US';
  const navigate = useNavigate();
  const weekReportChartRef = useRef<HTMLCanvasElement>(null);
  const usersChartRef = useRef<HTMLCanvasElement>(null);
  const ordersEvolutionChartRef = useRef<HTMLCanvasElement>(null);
  const weekReportChartInstance = useRef<Chart | null>(null);
  const usersChartInstance = useRef<Chart | null>(null);
  const ordersEvolutionChartInstance = useRef<Chart | null>(null);
  
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [timeSeries, setTimeSeries] = useState<TimeSeriesPoint[]>([]);
  const [timeSeriesThisWeek, setTimeSeriesThisWeek] = useState<TimeSeriesPoint[]>([]);
  const [timeSeriesLastWeek, setTimeSeriesLastWeek] = useState<TimeSeriesPoint[]>([]);
  const [topProducts, setTopProducts] = useState<TopProduct[]>([]);
  const [ordersEvolutionData, setOrdersEvolutionData] = useState<TimeSeriesPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // États pour le filtre
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all'); // 'all', 'stock', 'stock-out'
  const [sortBy, setSortBy] = useState<string>('orders'); // 'orders', 'name', 'price'
  const [weekTab, setWeekTab] = useState<'this' | 'last'>('this'); // 'this' pour cette semaine, 'last' pour la semaine dernière
  const [ordersPeriod, setOrdersPeriod] = useState<'day' | 'week' | 'month'>('day'); // Période pour l'évolution des commandes

  // Filtrer et trier les produits
  const filteredAndSortedProducts = useMemo(() => {
    let filtered = [...topProducts];

    // Filtrer par statut de stock
    if (filterStatus === 'stock') {
      filtered = filtered.filter(p => p.status === 'stock');
    } else if (filterStatus === 'stock-out') {
      filtered = filtered.filter(p => p.status === 'stock-out');
    }

    // Trier
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'orders':
          return b.totalOrders - a.totalOrders;
        case 'name':
          return a.name.localeCompare(b.name);
        case 'price':
          return b.price - a.price;
        default:
          return 0;
      }
    });

    return filtered;
  }, [topProducts, filterStatus, sortBy]);

  // Charger les données
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('🔄 Dashboard - Début du chargement des données...');
        
        // Charger toutes les données en parallèle, y compris les deux périodes pour le graphique
        const [statsData, timeSeriesThisWeekData, timeSeriesLastWeekData, topProductsData, ordersEvolutionDataLoaded] = await Promise.all([
          dashboardService.getStats(),
          dashboardService.getTimeSeries('this'),
          dashboardService.getTimeSeries('last'),
          dashboardService.getTopProducts(5),
          dashboardService.getOrdersEvolution(ordersPeriod),
        ]);
        
        console.log('✅ Dashboard - Données chargées:', {
          stats: statsData,
          timeSeriesThisWeek: timeSeriesThisWeekData.length,
          timeSeriesLastWeek: timeSeriesLastWeekData.length,
          topProducts: topProductsData.length,
        });
        
        setStats(statsData);
        setTimeSeriesThisWeek(timeSeriesThisWeekData);
        setTimeSeriesLastWeek(timeSeriesLastWeekData);
        setTimeSeries(weekTab === 'this' ? timeSeriesThisWeekData : timeSeriesLastWeekData);
        setTopProducts(topProductsData);
        if (ordersEvolutionDataLoaded) {
          setOrdersEvolutionData(ordersEvolutionDataLoaded);
        }
      } catch (err: any) {
        console.error('❌ Erreur lors du chargement des données:', err);
        console.error('❌ Détails de l\'erreur:', err.response || err.message || err);
        setError(err.message || t('dashboardPage.errors.loadingData'));
        // Mettre des valeurs par défaut pour éviter un écran vide
        setStats({
          totalSales: 0,
          totalSalesPrevious: 0,
          salesChange: 0,
          totalOrders: 0,
          totalOrdersPrevious: 0,
          ordersChange: 0,
          pendingOrders: 0,
          pendingOrdersPrevious: 0,
          pendingChange: 0,
          cancelledOrders: 0,
          cancelledOrdersPrevious: 0,
          cancelledChange: 0,
          totalClients: 0,
          totalProducts: 0,
          productsInStock: 0,
          productsOutOfStock: 0,
          totalRevenue: 0,
        });
        setTimeSeries([]);
        setTopProducts([]);
      } finally {
        setLoading(false);
        console.log('🏁 Dashboard - Chargement terminé');
      }
    };
    
    loadData();
  }, []); // Charger une seule fois au montage

  // Mettre à jour uniquement le graphique quand on change d'onglet (sans recharger la page)
  useEffect(() => {
    if (!weekReportChartInstance.current) return;
    
    const currentTimeSeries = weekTab === 'this' ? timeSeriesThisWeek : timeSeriesLastWeek;
    if (!currentTimeSeries.length) return;

    // Mettre à jour les labels
    const labels = currentTimeSeries.map(point => {
      const date = new Date(point.date);
      return getDayName(date, locale);
    });

    // Mettre à jour les données
    const salesData = currentTimeSeries.map(p => p.sales);
    const maxSales = Math.max(...salesData, 1);
    const maxValue = Math.ceil(maxSales * 1.2);

    // Mettre à jour le graphique existant
    const chart = weekReportChartInstance.current;
    chart.data.labels = labels;
    chart.data.datasets[0].data = salesData;
    chart.data.datasets[0].backgroundColor = currentTimeSeries.map((p) => {
      const maxVal = Math.max(...salesData);
      const opacity = maxVal > 0 ? 0.3 + (p.sales / maxVal) * 0.7 : 0.5;
      return `rgba(140, 24, 67, ${opacity})`;
    });
    
    // Mettre à jour l'échelle Y
    if (chart.options.scales && chart.options.scales.y) {
      chart.options.scales.y.max = maxValue;
    }

    // Mettre à jour le tooltip pour utiliser les nouvelles données
    if (chart.options.plugins && chart.options.plugins.tooltip && chart.options.plugins.tooltip.callbacks) {
      chart.options.plugins.tooltip.callbacks.label = function(context: any) {
        const value = context.parsed.y;
        const orders = currentTimeSeries[context.dataIndex]?.orders || 0;
        return [
          `${t('dashboardPage.chart.sales')}: ${formatCurrency(typeof value === 'number' ? value : 0, locale)}`,
          `${t('dashboardPage.chart.orders')}: ${orders}`
        ];
      };
    }

    // Animer la mise à jour
    chart.update('active');

    console.log('📊 Dashboard - Graphique mis à jour pour:', weekTab);
  }, [weekTab, timeSeriesThisWeek, timeSeriesLastWeek, locale, t]);

  // Créer le graphique initial une seule fois
  useEffect(() => {
    if (!timeSeries.length || !weekReportChartRef.current) return;

    // Détruire l'ancien graphique s'il existe
    if (weekReportChartInstance.current) {
      weekReportChartInstance.current.destroy();
      weekReportChartInstance.current = null;
    }

    // Créer les labels de jours
    const labels = timeSeries.map(point => {
      const date = new Date(point.date);
      return getDayName(date, locale);
    });

    // Créer le graphique avec les données réelles
    if (weekReportChartRef.current) {
      const salesData = timeSeries.map(p => p.sales);
      const maxSales = Math.max(...salesData, 1);
      const maxValue = Math.ceil(maxSales * 1.2); // Ajouter 20% de marge
      
      console.log('📊 Dashboard - Données pour le graphique:', {
        labels,
        salesData,
        maxSales,
        maxValue,
        timeSeries
      });
      
      weekReportChartInstance.current = new Chart(weekReportChartRef.current, {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label: t('dashboardPage.chart.sales'),
            data: timeSeries.map(p => p.sales),
            backgroundColor: timeSeries.map((p) => {
              // Dégradé de couleur : plus foncé pour les valeurs plus élevées
              const maxVal = Math.max(...salesData);
              const opacity = maxVal > 0 ? 0.3 + (p.sales / maxVal) * 0.7 : 0.5;
              return `rgba(140, 24, 67, ${opacity})`;
            }),
            borderColor: '#8C1843',
            borderWidth: 2,
            borderRadius: 6,
            borderSkipped: false,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            },
            tooltip: {
              backgroundColor: 'rgba(0, 0, 0, 0.85)',
              padding: 14,
              titleFont: {
                size: 14,
                weight: 'bold'
              },
              bodyFont: {
                size: 13,
                weight: 'normal' as const
              },
              displayColors: true,
              callbacks: {
                title: function(context) {
                  return context[0].label;
                },
                label: function(context) {
                  const value = context.parsed.y;
                  const orders = timeSeries[context.dataIndex]?.orders || 0;
                  return [
                    `${t('dashboardPage.chart.sales')}: ${formatCurrency(typeof value === 'number' ? value : 0, locale)}`,
                    `${t('dashboardPage.chart.orders')}: ${orders}`
                  ];
                },
                labelColor: function() {
                  return {
                    borderColor: '#8C1843',
                    backgroundColor: '#8C1843'
                  };
                }
              }
            }
          },
          scales: {
            x: {
              grid: {
                display: false
              },
              ticks: {
                font: {
                  size: 12,
                  weight: 500
                },
                color: '#666'
              }
            },
            y: {
              beginAtZero: true,
              max: maxValue,
              grid: {
                color: 'rgba(0, 0, 0, 0.05)'
              },
              ticks: {
                callback: function(value) {
                  const numValue = typeof value === 'number' ? value : 0;
                  // Formater les grandes valeurs de manière plus compacte
                  if (numValue >= 1000) {
                    return (numValue / 1000).toFixed(1) + ` ${t('dashboardPage.chart.kEuroSuffix')}`;
                  }
                  return formatCurrency(numValue, locale);
                },
                font: {
                  size: 11
                },
                color: '#666',
                padding: 8
              }
            }
          },
          animation: {
            duration: 1000,
            easing: 'easeOutQuart'
          }
        }
      });
    }

    return () => {
      if (weekReportChartInstance.current) {
        weekReportChartInstance.current.destroy();
        weekReportChartInstance.current = null;
      }
    };
  }, [timeSeries, locale, t]);

  // Graphique des utilisateurs (simplifié pour l'instant)
  useEffect(() => {
    if (!usersChartRef.current) return;

    // Détruire l'ancien graphique s'il existe
    if (usersChartInstance.current) {
      usersChartInstance.current.destroy();
      usersChartInstance.current = null;
    }

    // Données simplifiées pour le graphique utilisateurs (peut être amélioré plus tard)
    if (usersChartRef.current) {
      usersChartInstance.current = new Chart(usersChartRef.current, {
        type: 'bar',
        data: {
          labels: ['0', '5', '10', '15', '20', '25', '30'],
          datasets: [{
            label: t('dashboardPage.users'),
            data: [12, 19, 15, 25, 22, 30, 21.5],
            backgroundColor: '#8C1843',
            borderRadius: 4,
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            }
          },
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback: function(value) {
                  return value + 'k';
                }
              }
            }
          }
        }
      });
    }

    return () => {
      if (usersChartInstance.current) {
        usersChartInstance.current.destroy();
        usersChartInstance.current = null;
      }
    };
  }, [t]);

  return (
    <>

    {error && (
              <Alert type="error" message={error} onClose={() => setError(null)} />
            )}
            
            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <p>{t('dashboardPage.loadingData')}</p>
              </div>
            ) : !stats ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <Alert 
                  type="warning" 
                  message={t('dashboardPage.noDataMessage')}
                />
              </div>
            ) : (
              <>
                {/* KPI Cards Row */}
                <div className="kpi-row">
                  {/* Ventes totales */}
                  <div className="kpi-card">
                    <div className="kpi-header">
                      <h3 className="kpi-title">{t('dashboardPage.kpi.totalSales')}</h3>
                      <span className="kpi-period">{t('dashboardPage.allOrders')}</span>
                    </div>
                    <div className="kpi-value">{stats ? formatCurrency(stats.totalSales, locale) : formatCurrency(0, locale)}</div>
                    <div className={`kpi-change ${stats && stats.salesChange >= 0 ? 'positive' : 'negative'}`}>
                      <span className="change-icon">{stats && stats.salesChange >= 0 ? '↑' : '↓'}</span>
                      <span className="change-value">
                        {stats ? Math.abs(stats.salesChange).toFixed(1) : '0'}%
                      </span>
                    </div>
                    <div className="kpi-comparison">
                      {t('dashboardPage.previous7Days')} ({stats ? formatCurrency(stats.totalSalesPrevious, locale) : formatCurrency(0, locale)})
                    </div>
                    <button 
                      className="kpi-details-btn"
                      onClick={() => navigate('/admin/rapport')}
                    >
                      {t('dashboardPage.details')}
                    </button>
                  </div>

                  {/* Commandes totales */}
                  <div className="kpi-card">
                    <div className="kpi-header">
                      <h3 className="kpi-title">{t('dashboardPage.kpi.totalOrders')}</h3>
                      <span className="kpi-period">{t('dashboardPage.allOrders')}</span>
                    </div>
                    <div className="kpi-value">{stats ? formatNumber(stats.totalOrders) : '0'}</div>
                    <div className={`kpi-change ${stats && stats.ordersChange >= 0 ? 'positive' : 'negative'}`}>
                      <span className="change-icon">{stats && stats.ordersChange >= 0 ? '↑' : '↓'}</span>
                      <span className="change-value">
                        {stats ? Math.abs(stats.ordersChange).toFixed(1) : '0'}%
                      </span>
                    </div>
                    <div className="kpi-comparison">
                      {t('dashboardPage.previous7Days')} ({stats ? formatNumber(stats.totalOrdersPrevious) : '0'})
                    </div>
                    <button 
                      className="kpi-details-btn"
                      onClick={() => navigate('/admin/commandes')}
                    >
                      {t('dashboardPage.details')}
                    </button>
                  </div>

                  {/* En attente */}
                  <div className="kpi-card">
                    <div className="kpi-header">
                      <h3 className="kpi-title">{t('dashboardPage.kpi.pendingOrders')}</h3>
                      <span className="kpi-period">{t('dashboardPage.last7Days')}</span>
                    </div>
                    <div className="kpi-value">{stats ? stats.pendingOrders : '0'}</div>
                    <div className={`kpi-change ${stats && stats.pendingChange >= 0 ? 'positive' : 'negative'}`}>
                      <span className="change-icon">{stats && stats.pendingChange >= 0 ? '↑' : '↓'}</span>
                      <span className="change-value">
                        {stats ? Math.abs(stats.pendingChange).toFixed(1) : '0'}%
                      </span>
                    </div>
                    <div className="kpi-comparison">
                      {t('dashboardPage.previous7Days')} ({stats ? stats.pendingOrdersPrevious : '0'})
                    </div>
                    <button 
                      className="kpi-details-btn"
                      onClick={() => navigate('/admin/commandes?status=pending')}
                    >
                      {t('dashboardPage.details')}
                    </button>
                  </div>

                  {/* Annulés */}
                  <div className="kpi-card">
                    <div className="kpi-header">
                      <h3 className="kpi-title">{t('dashboardPage.kpi.cancelledOrders')}</h3>
                      <span className="kpi-period">{t('dashboardPage.allOrders')}</span>
                    </div>
                    <div className="kpi-value">{stats ? stats.cancelledOrders : '0'}</div>
                    <div className={`kpi-change ${stats && stats.cancelledChange >= 0 ? 'positive' : 'negative'}`}>
                      <span className="change-icon">{stats && stats.cancelledChange >= 0 ? '↑' : '↓'}</span>
                      <span className="change-value">
                        {stats ? Math.abs(stats.cancelledChange).toFixed(1) : '0'}%
                      </span>
                    </div>
                    <div className="kpi-comparison">
                      {t('dashboardPage.previous7Days')} ({stats ? stats.cancelledOrdersPrevious : '0'})
                    </div>
                    <button 
                      className="kpi-details-btn"
                      onClick={() => navigate('/admin/commandes?status=cancelled')}
                    >
                      {t('dashboardPage.details')}
                    </button>
                  </div>
                </div>

                {/* Middle Row */}
                <div className="middle-row">
              {/* Rapport de cette semaine */}
              <div className="report-card">
                <div className="report-tabs">
                  <button 
                    className={`report-tab ${weekTab === 'this' ? 'active' : ''}`}
                    onClick={() => setWeekTab('this')}
                  >
                    {t('dashboardPage.thisWeek')}
                  </button>
                  <button 
                    className={`report-tab ${weekTab === 'last' ? 'active' : ''}`}
                    onClick={() => setWeekTab('last')}
                  >
                    {t('dashboardPage.lastWeek')}
                  </button>
                </div>
                <div className="report-summary">
                  <div className="summary-item">
                    <div className="summary-value">{stats ? formatNumber(stats.totalClients) : '0'}</div>
                    <div className="summary-label">{t('dashboardPage.summary.clients')}</div>
                  </div>
                  <div className="summary-item">
                    <div className="summary-value">{stats ? formatNumber(stats.totalProducts) : '0'}</div>
                    <div className="summary-label">{t('dashboardPage.summary.totalProducts')}</div>
                  </div>
                  <div className="summary-item">
                    <div className="summary-value">{stats ? formatNumber(stats.productsInStock) : '0'}</div>
                    <div className="summary-label">{t('dashboardPage.summary.productsInStock')}</div>
                  </div>
                  <div className="summary-item">
                    <div className="summary-value">{stats ? formatNumber(stats.productsOutOfStock) : '0'}</div>
                    <div className="summary-label">{t('dashboardPage.summary.outOfStock')}</div>
                  </div>
                  <div className="summary-item">
                    <div className="summary-value">{stats ? formatCurrency(stats.totalRevenue, locale) : formatCurrency(0, locale)}</div>
                    <div className="summary-label">{t('dashboardPage.summary.revenue')}</div>
                  </div>
                </div>
                <div className="report-chart">
                  <canvas ref={weekReportChartRef} id="weekReportChart"></canvas>
                </div>
              </div>

                  {/* Right Column */}
                  <div className="right-column">
                {/* Utilisateurs au cours des 30 dernières minutes */}
                <div className="users-card">
                  <h3 className="card-title">{t('dashboardPage.usersLast30Minutes')}</h3>
                  <div className="users-value">{stats ? formatNumber(stats.totalClients) : '0'}</div>
                  <div className="users-subtitle">{t('dashboardPage.uniqueUsers')}</div>
                  <div className="users-chart">
                    <canvas ref={usersChartRef} id="usersChart"></canvas>
                  </div>
                </div>

                {/* Meilleurs produits */}
                <div className="best-products-card">
                  <div className="card-header">
                    <h3 className="card-title">{t('dashboardPage.bestProducts')}</h3>
                    <a href="#all-products" className="card-link">{t('dashboardPage.allProducts')}</a>
                  </div>
                  <div className="products-search">
                    <svg className="search-icon-small" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="16" height="16">
                      <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                    </svg>
                    <input type="text" className="products-search-input" placeholder={t('dashboardPage.search')} />
                  </div>
                  <div className="products-list">
                    {topProducts.length > 0 ? (
                      topProducts.map((product) => (
                        <div key={product.id} className="product-item">
                          <div className="product-info">
                            <div className="product-name">{product.name}</div>
                            <div className="product-details">
                              <span className="product-article">{t('dashboardPage.ordersLabel')}: {product.totalOrders}</span>
                              <span className="product-price">{formatCurrency(product.price, locale)}</span>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="product-item">
                        <div className="product-info">
                          <div className="product-name">{t('dashboardPage.noProductSold')}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Row - Top Selling Products Table */}
            <div className="table-card">
              <div className="table-header">
                <h3 className="table-title">{t('dashboardPage.topSellingProducts')}</h3>
                <button 
                  className="filter-btn"
                  onClick={() => setIsFilterModalOpen(true)}
                  aria-label={t('dashboardPage.filterProducts')}
                >
                  <FunnelIcon className="w-5 h-5" />
                  {t('dashboardPage.filter')}
                </button>
              </div>
              <table className="products-table">
                <thead>
                  <tr>
                    <th>{t('dashboardPage.table.product')}</th>
                    <th>{t('dashboardPage.table.totalOrders')}</th>
                    <th>{t('dashboardPage.table.status')}</th>
                    <th>{t('dashboardPage.table.price')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSortedProducts.length > 0 ? (
                    filteredAndSortedProducts.map((product) => (
                      <tr key={product.id}>
                        <td>{product.name}</td>
                        <td>{product.totalOrders}</td>
                        <td>
                          <span className={`status-badge ${product.status === 'stock' ? 'stock' : 'stock-out'}`}>
                            • {product.status === 'stock' ? t('dashboardPage.status.stock') : t('dashboardPage.status.stockOut')}
                          </span>
                        </td>
                        <td>{formatCurrency(product.price, locale)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} style={{ textAlign: 'center', padding: '20px' }}>
                        {topProducts.length === 0 ? t('dashboardPage.noProductSold') : t('dashboardPage.noProductMatchesFilters')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
                  <div className="table-footer">
                    <button 
                      className="details-btn"
                      onClick={() => navigate('/admin/liste-produits')}
                    >
                      {t('dashboardPage.details')}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* Modal de filtre */}
            {isFilterModalOpen && (
              <div
                className="modal-overlay"
                onClick={() => setIsFilterModalOpen(false)}
                role="dialog"
                aria-modal="true"
                aria-labelledby="filter-modal-title"
              >
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3 id="filter-modal-title" className="modal-title">{t('dashboardPage.filterProducts')}</h3>
                    <button
                      className="modal-close-btn"
                      onClick={() => setIsFilterModalOpen(false)}
                      aria-label={t('dashboardPage.close')}
                    >
                      <XMarkIcon className="w-6 h-6" />
                    </button>
                  </div>
                  <div className="modal-body">
                    {/* Filtre par statut de stock */}
                    <div className="form-group">
                      <label className="form-label">{t('dashboardPage.stockStatus')}</label>
                      <select
                        className="form-select"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                      >
                        <option value="all">{t('dashboardPage.filterOptions.all')}</option>
                        <option value="stock">{t('dashboardPage.filterOptions.inStock')}</option>
                        <option value="stock-out">{t('dashboardPage.filterOptions.outOfStock')}</option>
                      </select>
                    </div>

                    {/* Trier par */}
                    <div className="form-group">
                      <label className="form-label">{t('dashboardPage.sortBy')}</label>
                      <select
                        className="form-select"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                      >
                        <option value="orders">{t('dashboardPage.sortOptions.ordersDesc')}</option>
                        <option value="name">{t('dashboardPage.sortOptions.nameAsc')}</option>
                        <option value="price">{t('dashboardPage.sortOptions.priceDesc')}</option>
                      </select>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setFilterStatus('all');
                        setSortBy('orders');
                      }}
                    >
                      {t('dashboardPage.reset')}
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => setIsFilterModalOpen(false)}
                    >
                      {t('dashboardPage.apply')}
                    </button>
                  </div>
                </div>
              </div>
            )}
  
    </>
  );
};

